import { useMemo, useState } from 'react';
import { IconBan, IconChevronLeft, IconClock, IconCopy, IconMapPin, IconPencil, IconRefresh, IconRoute, IconRuler, IconTrafficCone, IconTruck, IconCheck, IconGps, IconPackage, IconListCheck, IconExternalLink, type Icon } from '@tabler/icons-react';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useT } from '@hana/i18n';
import { whRestrict } from './places';
import { CARGO_COLOR, CARGO_LABEL, LEG, MAP_ROUTES, ROUTE_TOTAL, TOLLS, TOLL_COLOR, TOLL_TOTAL, cai, fmtM, filterRoutes, filterTolls, plansOf, rangeBar, routePlace, routeStatus, routesOfToll, timeOf, tollByName, tollStatus, whOf, type MapRoute, type RtSub, type TollStation, type TollSub } from './mapRoutes';
import s from './places.module.css';

// Mint-TMS-Places · loại Tuyến và Trạm thu phí (panel danh sách + chi tiết). Nút ghi chỉ báo toast (chưa ghi DB).
// Dòng tiền (Giá chuyến, Lương TX, luật chặng PAY_*) ẩn: màn chưa có quyền money (board money=hide).

const STATUS_DOT = { ok: 'var(--ok-dot)', warn: 'var(--d-amber)', err: 'var(--err)' } as const;
const STATUS_CHIP = { ok: s.stOk, warn: s.stWarn, err: s.stErr } as const;
const tint = (c: string) => `color-mix(in srgb, ${c} 12%, transparent)`;

function Sec({ icon: Ico, title, hint, children }: { icon: Icon; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className={s.sec}>
      <h3 className={s.secH}>
        <Ico size={14} stroke={2} aria-hidden="true" />
        {title}
        {hint && <span role="img" aria-label={hint} title={hint} className={s.info}>i</span>}
      </h3>
      {children}
    </section>
  );
}

interface Row { key: string; k: string; v?: string; num?: string; dot: string; tag?: string; open?: () => void }
function Rows({ rows }: { rows: readonly Row[] }) {
  const t = useT();
  return (
    <div className={s.list}>
      {rows.map((r) => (
        <div key={r.key} className={s.lrow}>
          <i className={s.dot8} style={{ background: r.dot }} />
          <div className={s.lk}>{r.k}{r.v && <small className={s.lkSub}>{r.v}</small>}</div>
          {r.tag && <em className={s.rowTag}>{r.tag}</em>}
          {r.num && <b>{r.num}</b>}
          {r.open && <button type="button" className={s.lBtn} onClick={r.open}><IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('tms.open')}</button>}
        </div>
      ))}
    </div>
  );
}

function StatGrid({ stats }: { stats: ReadonlyArray<[Icon, string, string | number, boolean]> }) {
  return (
    <div className={s.stats}>
      {stats.map(([I, k, v, on]) => (
        <div key={k} className={s.stat}>
          <div className={s.statK}><I size={13} stroke={2} aria-hidden="true" />{k}</div>
          <b className={on ? '' : s.dim}>{v}</b>
        </div>
      ))}
    </div>
  );
}

function DHead({ icon: Ico, color, title, tip, code, st, onBack }: { icon: Icon; color: string; title: string; tip: string; code: string; st: { key: 'ok' | 'warn' | 'err'; label: string }; onBack: () => void }) {
  return (
    <header className={s.dHead}>
      <button type="button" className={s.sq30} aria-label="Về danh sách" title="Về danh sách" onClick={onBack}><IconChevronLeft size={18} stroke={2} aria-hidden="true" /></button>
      <span className={s.dIc} title={tip} style={{ background: tint(color), color }}><Ico size={20} stroke={1.9} aria-hidden="true" /></span>
      <div className={s.dTx}>
        <h2 className={s.dTitle} title={tip}>{title}</h2>
        <div className={s.dMeta}>
          <span className={s.code}>{code}</span>
          <span className={`${s.chipSt} ${STATUS_CHIP[st.key]}`}><i style={{ background: STATUS_DOT[st.key] }} />{st.label}</span>
        </div>
      </div>
    </header>
  );
}

export function RtDetail({ r, approved, onApprove, onEdit, onBack, onOpenToll, onPrice }: { r: MapRoute; approved: boolean; onApprove: () => void; onEdit: () => void; onBack: () => void; onOpenToll: (id: string) => void; onPrice: () => void }) {
  const toast = useToast();
  const t = useT();
  const w = whOf(r.wh);
  const dp = cai();
  const st = routeStatus(r, approved);
  const tm = timeOf(r);
  const nL = r.tolls.reduce((a, x) => a + (x[2] || 1), 0);
  const wr = w ? whRestrict(w) : null;
  const pl = plansOf(r);
  const plW = pl.filter((p) => p.warn);
  const bar = tm.gps ? rangeBar(tm) : null;
  const ok = r.ok || approved;
  return (
    <section aria-label={`${dp.name} → ${w?.name ?? r.wh}`} className={`${s.panel} ${s.detail}`}>
      <DHead icon={IconRoute} color={CARGO_COLOR[r.cargo]} title={`${dp.name} → ${w?.name ?? r.wh}`} tip={`${CARGO_LABEL[r.cargo]} · vòng về cảng cũ · bậc lương ${r.tier}`} code={r.id} st={st} onBack={onBack} />
      <div className={s.dBody}>
        <div className={s.acts}>
          {!ok && <button type="button" className={s.act} onClick={onApprove}><IconCheck size={16} stroke={1.8} aria-hidden="true" />{t('tms.approveRoute')}</button>}
          <button type="button" className={`${s.act} ${ok ? '' : s.actGhost}`} onClick={onEdit}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('tms.edit')}</button>
          <button type="button" className={`${s.act} ${s.actGhost}`} onClick={onPrice}><IconListCheck size={16} stroke={1.8} aria-hidden="true" />{t('tms.priceTrf')}</button>
        </div>
        <StatGrid stats={[
          [IconRuler, 'Km', String(r.km).replace('.', ','), true],
          [IconClock, tm.gps ? 'Thời gian chạy · TB' : 'Thời gian · ước tính', tm.k, tm.gps],
          [IconTrafficCone, 'Trạm · lượt', r.tolls.length ? `${r.tolls.length} · ${nL}` : '0', r.tolls.length > 0],
          [IconPackage, 'Chuyến', r.trips, r.trips > 0],
        ]} />
        <Sec icon={IconClock} title="Thời gian chạy" hint={tm.gps ? 'TB các chuyến đã chạy · GPS hành trình xe (StartTrip → EndTrip) · tự cập nhật sau mỗi chuyến' : 'Chưa có chuyến GPS · ước tính VietMap theo đường xe tải · tự chuyển sang thực tế từ chuyến đầu tiên'}>
          <div className={s.rng}>
            <div className={s.rngTop}>
              <b>{tm.k}</b>
              <span className={`${s.rngSrc} ${tm.gps ? s.stOk : s.rngEst}`} title={tm.gps ? `${tm.n} / ${tm.all} chuyến có GPS` : 'Tuyến chưa có chuyến GPS'}>
                {tm.gps ? <IconGps size={12} stroke={2.2} aria-hidden="true" /> : <IconMapPin size={12} stroke={2.2} aria-hidden="true" />}
                {tm.gps ? `${tm.n} chuyến · GPS` : 'Ước tính · VietMap'}
              </span>
              {tm.gps && <span className={s.rngUpd} title="Cập nhật gần nhất">{tm.upd}</span>}
            </div>
            {bar && (
              <>
                <div role="img" aria-label={`Nhanh nhất ${fmtM(tm.min)}, chậm nhất ${fmtM(tm.max)}, trung bình ${tm.k}`} title={`Nhanh nhất ${fmtM(tm.min)}, chậm nhất ${fmtM(tm.max)}, trung bình ${tm.k}`} className={s.rngBar}>
                  <span style={{ left: `${bar.l}%`, width: `${bar.w}%` }} />
                  <i style={{ left: `${bar.a}%` }} />
                </div>
                <div className={s.rngEnds}><span>{`Nhanh ${fmtM(tm.min)}`}</span><span>{`Chậm ${fmtM(tm.max)}`}</span></div>
              </>
            )}
          </div>
        </Sec>
        {pl.length > 0 && (
          <Sec icon={IconPackage} title="Lệnh điều xe sắp chạy" hint="Kiểm theo hạn chế xe của kho khi điều độ xếp xe">
            <Rows rows={pl.map((p) => ({ key: p.sid, k: `${p.sid} · ${p.veh}`, v: p.warn || 'Hợp lệ', num: p.at.slice(0, 6) + ' ' + (p.at.split(' ')[1] ?? ''), dot: p.warn ? 'var(--err)' : 'var(--ok-dot)', open: () => toast({ msg: `Mở ${p.sid} ở bảng điều xe` }) }))} />
            {plW.length > 0 && <div className={`${s.note} ${s.noteErr}`}>{plW.map((p) => `${p.sid} · ${p.warn}`).join(' · ')}</div>}
          </Sec>
        )}
        {wr && w && (
          <Sec icon={IconTruck} title="Hạn chế xe tại kho" hint={`Theo kho ${w.code} · sửa ở chi tiết kho`}>
            <div className={s.tags}>
              {wr.ban.map((b) => <span key={b} className={s.tag} title="Giờ cấm tải"><IconClock size={12} stroke={2} aria-hidden="true" />{b}</span>)}
              {wr.ton ? <span className={s.tag} title="Tải trọng tối đa">{`≤ ${wr.ton} tấn`}</span> : null}
              {wr.veh.map((v) => <span key={v} className={s.tag} title="Được vào kho"><IconTruck size={12} stroke={2} aria-hidden="true" />{v}</span>)}
              {wr.road ? <span className={s.tag} title="Đường / khu cấm xe lớn"><IconBan size={12} stroke={2} aria-hidden="true" />{wr.road}</span> : null}
            </div>
          </Sec>
        )}
        <Sec icon={IconRoute} title="Chuỗi điểm" hint="Vòng về cảng cũ (StopsJson)">
          <Rows rows={[
            { key: '1', k: `1 · ${dp.name}`, v: `Lấy rỗng / hạ hàng · ${dp.code}`, dot: 'var(--tx2)' },
            { key: '2', k: `2 · ${w?.name ?? r.wh} · ${r.wh}`, v: (w?.area ?? '') + (w && w.lat == null ? ' · thiếu toạ độ' : ''), dot: w && w.lat == null ? 'var(--err)' : 'var(--ok-dot)' },
            { key: '3', k: `3 · ${dp.name}`, v: 'Về cảng', dot: 'var(--tx2)' },
          ]} />
        </Sec>
        <Sec icon={IconTrafficCone} title="Trạm thu phí trên tuyến" hint={r.tolls.length ? 'TollJson · bảng giá VETC / ePass · ● thu mở  ● thu kín' : undefined}>
          {r.tolls.length ? (
            <Rows rows={r.tolls.map(([k, he, qq, low]) => { const s0 = tollByName(k); return { key: k, k, v: `${he === 'KIN' ? 'Thu kín' : 'Thu mở'} · ${qq} lượt`, num: `×${qq}`, dot: TOLL_COLOR[he], tag: low ? 'Toạ độ tin cậy thấp' : undefined, open: s0 ? () => onOpenToll(s0.id) : undefined }; })} />
          ) : <div className={s.note}>{t('tms.noToll')}</div>}
        </Sec>
        <Sec icon={IconListCheck} title="Luật chặng" hint="Áp chung mọi tuyến · hiệu lực 01/Oct/2026 · ● NeedVerify">
          <Rows rows={LEG.filter((y) => !y[3]).map(([k, v, val]) => ({ key: k, k: v, v: `${k} · NeedVerify`, num: val, dot: 'var(--d-amber)' }))} />
        </Sec>
      </div>
    </section>
  );
}

export function TollDetail({ x, onEdit, onBack, onOpenRoute }: { x: TollStation; onEdit: () => void; onBack: () => void; onOpenRoute: (id: string) => void }) {
  const toast = useToast();
  const t = useT();
  const st = tollStatus(x);
  const rts = routesOfToll(x);
  const conf = x.conf === 'thap' ? 'Thấp' : x.conf === 'tb' ? 'Trung bình' : 'Cao';
  return (
    <section aria-label={x.name} className={`${s.panel} ${s.detail}`}>
      <DHead icon={IconTrafficCone} color={TOLL_COLOR[x.sys]} title={x.name} tip={`Trạm thu phí · ${x.sys === 'KIN' ? 'Thu kín' : 'Thu mở'}`} code={x.corridor !== '—' ? x.corridor : x.id} st={st} onBack={onBack} />
      <div className={s.dBody}>
        <div className={s.acts}>
          <button type="button" className={s.act} onClick={onEdit}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('tms.edit')}</button>
          <button type="button" className={`${s.act} ${s.actGhost}`} onClick={() => toast({ msg: `Đã sao ${x.lat}, ${x.lng}` })}><IconCopy size={16} stroke={1.8} aria-hidden="true" />{t('tms.copyCoords')}</button>
        </div>
        <StatGrid stats={[
          [IconTrafficCone, 'Hệ thống', x.sys === 'KIN' ? 'Thu kín' : 'Thu mở', true],
          [IconRoute, 'Hành lang', x.corridor, x.corridor !== '—'],
          [IconPackage, 'Tuyến đi qua', rts.length || '—', rts.length > 0],
          [IconMapPin, 'Tin cậy', conf, x.conf !== 'thap'],
        ]} />
        <Sec icon={IconMapPin} title="Vị trí" hint="Nguồn toạ độ: osm / vietmap / nhập tay">
          <dl className={s.kv}><dt>{t('tms.coords')}</dt><dd className={s.coord}>{`${x.lat.toFixed(4)}, ${x.lng.toFixed(4)}`}</dd></dl>
          {x.conf === 'thap' && <div className={`${s.note} ${s.noteWarn}`}>{t('tms.lowConfWarn')}</div>}
        </Sec>
        <Sec icon={IconRoute} title="Tuyến đi qua trạm">
          {rts.length ? (
            <Rows rows={rts.map((r) => ({ key: r.id, k: r.id, v: whOf(r.wh)?.name ?? r.wh, num: `×${r.tolls.find((y) => y[0] === x.name)?.[2] ?? 1}`, dot: CARGO_COLOR[r.cargo], open: () => onOpenRoute(r.id) }))} />
          ) : <div className={s.note}>{t('tms.noRouteForToll')}</div>}
        </Sec>
      </div>
    </section>
  );
}

interface PanelBase { q: string; state: ScreenState; onRetry: () => void }
function Skel() { return <div aria-busy="true" className={s.skel}>{[1, 2, 3, 4, 5, 6, 7, 8].map((k) => <Skeleton key={k} />)}</div>; }

export function RtListPanel({ q, state, onRetry, approved, onPick }: PanelBase & { approved: ReadonlySet<string>; onPick: (id: string) => void }) {
  const toast = useToast();
  const t = useT();
  const [sub, setSub] = useState<RtSub>('all');
  const [pend, setPend] = useState(false);
  const list = useMemo(() => filterRoutes(MAP_ROUTES, sub, q, pend, approved), [sub, q, pend, approved]);
  const nPend = MAP_ROUTES.filter((r) => !(r.ok || approved.has(r.id))).length;
  const count = (k: RtSub) => filterRoutes(MAP_ROUTES, k, '', false, approved).length;
  const subs: ReadonlyArray<readonly [RtSub, string]> = [['all', 'Tất cả'], ['RF', 'Hàng lạnh'], ['DC', 'Hàng khô']];
  return (
    <section aria-label="Tuyến" className={`${s.panel} ${s.lp}`}>
      <div className={s.lpHead}>
        <div className={s.lpTop}>
          <h2 className={s.lpH}>{t('tms.kindRoute')}</h2>
          <span className={s.lpCount} title={`${list.length} tuyến đang hiện / ${ROUTE_TOTAL} trong danh mục`}>{`${list.length} / ${ROUTE_TOTAL}`}</span>
          <button type="button" className={s.sq30} aria-label="Làm mới" title="Làm mới" onClick={() => toast({ msg: 'Đã tải lại Tuyến' })}><IconRefresh size={15} stroke={2} aria-hidden="true" /></button>
        </div>
        <div className={s.pills}>
          {subs.map(([k, label]) => <button key={k} type="button" aria-pressed={sub === k} className={`${s.pill} ${sub === k ? s.pillOn : ''}`} onClick={() => setSub(k)}>{label}<span>{count(k)}</span></button>)}
          {nPend > 0 && <button type="button" aria-pressed={pend} className={`${s.pill} ${pend ? s.pillOn : ''}`} title="NeedVerify" onClick={() => setPend(!pend)}><i style={{ background: 'var(--d-amber)' }} />{nPend}<span>NeedVerify</span></button>}
        </div>
      </div>
      <div role="listbox" aria-label="Tuyến" className={s.lpBody}>
        <StateView state={state} icon={<IconRoute size={26} stroke={1.8} />} title={state === 'empty' ? 'Chưa có tuyến nào' : undefined} action={state === 'error' ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined} skeleton={<Skel />}>
          {(['RF', 'DC'] as const).map((cg) => {
            const its = list.filter((r) => r.cargo === cg);
            if (!its.length) return null;
            return (
              <div key={cg}>
                <div className={s.grp}><i style={{ background: CARGO_COLOR[cg] }} />{`${CARGO_LABEL[cg]} · ${cg}`}<span>{its.length}</span></div>
                {its.map((r) => {
                  const st = routeStatus(r, approved.has(r.id));
                  const tm = timeOf(r);
                  const w = whOf(r.wh);
                  const warn = plansOf(r).find((p) => p.warn)?.warn;
                  return (
                    <button key={r.id} type="button" role="option" aria-selected={false} className={s.item} title={[r.id, `Cát Lái ⇄ ${w?.area ?? ''} · ${r.tier}`, st.label].join(' · ')} onClick={() => onPick(r.id)}>
                      <span className={s.itIc} style={{ background: tint(CARGO_COLOR[cg]), color: CARGO_COLOR[cg] }}><IconRoute size={16} stroke={1.9} aria-hidden="true" /></span>
                      <b>{w?.name ?? r.wh}</b>
                      <span className={s.mets}>
                        <span title={`${String(r.km).replace('.', ',')} km`}><IconRuler size={13} stroke={2} aria-hidden="true" />{String(r.km).replace('.', ',')}</span>
                        <span title={tm.tip} style={tm.gps ? undefined : { color: 'var(--lb)' }}><IconClock size={13} stroke={2} aria-hidden="true" />{tm.short}</span>
                        {warn ? <span title={warn} style={{ color: 'var(--err)' }}><IconBan size={13} stroke={2} aria-hidden="true" /></span> : r.tolls.length > 0 && <span title={`${r.tolls.length} trạm thu phí`}><IconTrafficCone size={13} stroke={2} aria-hidden="true" />{r.tolls.length}</span>}
                      </span>
                      <span role="img" aria-label={st.label} title={st.label} className={s.dot8} style={{ background: STATUS_DOT[st.key] }} />
                    </button>
                  );
                })}
              </div>
            );
          })}
          {list.length === 0 && <div className={s.none}>{t('tms.noPlaceMatch')}</div>}
        </StateView>
      </div>
    </section>
  );
}

export function TollListPanel({ q, state, onRetry, onPick }: PanelBase & { onPick: (id: string) => void }) {
  const toast = useToast();
  const t = useT();
  const [sub, setSub] = useState<TollSub>('all');
  const [pend, setPend] = useState(false);
  const list = useMemo(() => filterTolls(TOLLS, sub, q, pend), [sub, q, pend]);
  const nPend = TOLLS.filter((x) => x.conf === 'thap').length;
  const count = (k: TollSub) => filterTolls(TOLLS, k, '', false).length;
  const subs: ReadonlyArray<readonly [TollSub, string]> = [['all', 'Tất cả'], ['KIN', 'Thu kín'], ['MO', 'Thu mở']];
  return (
    <section aria-label="Trạm thu phí" className={`${s.panel} ${s.lp}`}>
      <div className={s.lpHead}>
        <div className={s.lpTop}>
          <h2 className={s.lpH}>{t('tms.kindToll')}</h2>
          <span className={s.lpCount} title={`${list.length} trạm đang hiện / ${TOLL_TOTAL} trong danh mục`}>{`${list.length} / ${TOLL_TOTAL}`}</span>
          <button type="button" className={s.sq30} aria-label="Làm mới" title="Làm mới" onClick={() => toast({ msg: 'Đã tải lại Trạm thu phí' })}><IconRefresh size={15} stroke={2} aria-hidden="true" /></button>
        </div>
        <div className={s.pills}>
          {subs.map(([k, label]) => <button key={k} type="button" aria-pressed={sub === k} className={`${s.pill} ${sub === k ? s.pillOn : ''}`} onClick={() => setSub(k)}>{label}<span>{count(k)}</span></button>)}
          {nPend > 0 && <button type="button" aria-pressed={pend} className={`${s.pill} ${pend ? s.pillOn : ''}`} title={t('tms.lowConf')} onClick={() => setPend(!pend)}><i style={{ background: 'var(--d-amber)' }} />{nPend}<span>{t('tms.lowConf')}</span></button>}
        </div>
      </div>
      <div role="listbox" aria-label="Trạm thu phí" className={s.lpBody}>
        <StateView state={state} icon={<IconTrafficCone size={26} stroke={1.8} />} title={state === 'empty' ? 'Chưa có trạm nào' : undefined} action={state === 'error' ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined} skeleton={<Skel />}>
          {(['KIN', 'MO'] as const).map((sy) => {
            const its = list.filter((x) => x.sys === sy);
            if (!its.length) return null;
            return (
              <div key={sy}>
                <div className={s.grp}><i style={{ background: TOLL_COLOR[sy] }} />{sy === 'KIN' ? 'Thu kín' : 'Thu mở'}<span>{its.length}</span></div>
                {its.map((x) => {
                  const st = tollStatus(x);
                  const n = routesOfToll(x).length;
                  return (
                    <button key={x.id} type="button" role="option" aria-selected={false} className={s.item} title={[x.corridor !== '—' ? x.corridor : x.id, st.label].join(' · ')} onClick={() => onPick(x.id)}>
                      <span className={s.itIc} style={{ background: tint(TOLL_COLOR[sy]), color: TOLL_COLOR[sy] }}><IconTrafficCone size={16} stroke={1.9} aria-hidden="true" /></span>
                      <b>{x.name}</b>
                      <span className={s.mets}>{n > 0 && <span title={`${n} tuyến đi qua`}><IconRoute size={13} stroke={2} aria-hidden="true" />{n}</span>}</span>
                      <span role="img" aria-label={st.label} title={st.label} className={s.dot8} style={{ background: STATUS_DOT[st.key] }} />
                    </button>
                  );
                })}
              </div>
            );
          })}
          {list.length === 0 && <div className={s.none}>{t('tms.noPlaceMatch')}</div>}
        </StateView>
      </div>
    </section>
  );
}

export { routePlace };
